import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { ArrowLeft, Trophy, Play, Users, Clock, Crown, Swords, Shield, RefreshCw, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getGuestId = () => {
  let guestId = sessionStorage.getItem('metal_lego_guest_id');
  if (!guestId) {
    guestId = 'guest_' + Math.random().toString(36).substr(2, 8);
    sessionStorage.setItem('metal_lego_guest_id', guestId);
  }
  return guestId;
};

const TournamentBracket = ({ tournamentId, onBack, onPlayMatch, onSpectateMatch }) => {
  const { publicKey } = useWallet();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);

  const playerId = publicKey?.toBase58() || getGuestId();
  const isHost = tournament?.host_id === playerId;

  const fetchTournament = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tournament/${tournamentId}`);
      setTournament(res.data);
    } catch (e) {
      console.error('Error fetching tournament:', e);
      toast.error('Failed to load tournament');
    } finally { setLoading(false); }
  }, [tournamentId]);

  useEffect(() => {
    fetchTournament();
    const interval = setInterval(fetchTournament, 5000);
    return () => clearInterval(interval);
  }, [fetchTournament]);

  const handleStart = async () => {
    try {
      await axios.post(`${API}/tournament/${tournamentId}/start?host_id=${playerId}`);
      toast.success('Tournament started!');
      fetchTournament();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start');
    }
  };

  // Find this player's active match
  const myActiveMatch = tournament?.bracket?.find(m =>
    m.status === 'active' &&
    ((m.player1 && m.player1.player_id === playerId) || (m.player2 && m.player2.player_id === playerId))
  );

  // Group bracket by rounds
  const rounds = {};
  if (tournament?.bracket) {
    tournament.bracket.forEach(m => {
      if (!rounds[m.round]) rounds[m.round] = [];
      rounds[m.round].push(m);
    });
  }
  const maxRound = Math.max(...Object.keys(rounds).map(Number), 0);

  const roundLabels = (round, total) => {
    if (round === total) return 'FINALS';
    if (round === total - 1) return 'SEMI-FINALS';
    if (round === total - 2) return 'QUARTER-FINALS';
    return `ROUND ${round}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#FF4444] font-game text-xl mb-4">Tournament not found</p>
          <Button onClick={onBack} className="game-btn"><ArrowLeft className="w-4 h-4 mr-2" /> Go Back</Button>
        </div>
      </div>
    );
  }

  const champion = tournament.status === 'completed' && tournament.bracket?.length > 0
    ? tournament.bracket[tournament.bracket.length - 1]?.winner
    : null;
  const championPlayer = champion ? tournament.players?.find(p => p.player_id === champion) : null;

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button onClick={onBack} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
          <Button onClick={fetchTournament} className="bg-[#333] hover:bg-[#444]" data-testid="refresh-bracket">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Tournament Info */}
        <div className="glass-card p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div>
              <h1 className="font-game text-2xl text-[#FFD700] mb-1 flex items-center gap-2">
                <Trophy className="w-6 h-6" /> {tournament.name}
              </h1>
              <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {tournament.players?.length}/{tournament.bracket_size}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {tournament.time_limit}s/match</span>
                <span className="capitalize font-game text-xs" style={{
                  color: tournament.status === 'registration' ? '#14F195' : tournament.status === 'in_progress' ? '#FF9500' : '#FFD700'
                }}>{tournament.status.replace('_', ' ')}</span>
                {tournament.current_round > 0 && <span className="text-[#9945FF]">Round {tournament.current_round}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              {tournament.status === 'registration' && isHost && (
                <Button onClick={handleStart} className="bg-[#14F195] text-black hover:bg-[#14F195]/90 font-game"
                  disabled={tournament.players?.length < 2} data-testid="start-tournament-btn">
                  <Play className="w-5 h-5 mr-2" />
                  {tournament.players?.length < 2 ? 'NEED PLAYERS' : 'START TOURNAMENT'}
                </Button>
              )}
              {myActiveMatch && (
                <Button onClick={() => onPlayMatch(tournamentId, myActiveMatch.match_id, tournament.time_limit)}
                  className="bg-[#FF9500] text-black hover:bg-[#FF9500]/90 font-game animate-pulse" data-testid="play-match-btn">
                  <Swords className="w-5 h-5 mr-2" /> PLAY YOUR MATCH
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Champion banner */}
        {championPlayer && (
          <div className="glass-card p-6 mb-6 border-2 border-[#FFD700] text-center" data-testid="champion-banner">
            <Crown className="w-12 h-12 text-[#FFD700] mx-auto mb-2" />
            <h2 className="font-game text-2xl text-[#FFD700]">CHAMPION</h2>
            <p className="font-game text-lg text-white mt-1">{championPlayer.name}</p>
          </div>
        )}

        {/* Players (registration phase) */}
        {tournament.status === 'registration' && (
          <div className="glass-card p-6 mb-6">
            <h2 className="font-game text-lg text-white mb-4">REGISTERED PLAYERS</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tournament.players?.map((p, i) => (
                <div key={p.player_id} className="bg-[#1a1a1a] rounded-lg p-3 border border-[#333] flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-[#FFD700]/20 flex items-center justify-center text-[#FFD700] font-game text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <span className="text-white text-sm font-game">{p.name}</span>
                    {p.player_id === tournament.host_id && <Crown className="w-3 h-3 text-[#FFD700] inline ml-1" />}
                    {p.player_id === playerId && <span className="text-[#14F195] text-xs ml-1">(You)</span>}
                  </div>
                </div>
              ))}
              {Array.from({ length: tournament.bracket_size - (tournament.players?.length || 0) }).map((_, i) => (
                <div key={`empty-${i}`} className="bg-[#0a0a0a] rounded-lg p-3 border border-dashed border-[#333] flex items-center justify-center">
                  <span className="text-[#333] text-xs">Open slot</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bracket View */}
        {tournament.bracket?.length > 0 && (
          <div className="glass-card p-6 overflow-x-auto">
            <h2 className="font-game text-lg text-white mb-4">BRACKET</h2>
            <div className="flex gap-8 min-w-max pb-4" data-testid="bracket-view">
              {Object.keys(rounds).sort((a, b) => a - b).map(roundNum => (
                <div key={roundNum} className="flex flex-col gap-2">
                  <h3 className="font-game text-xs text-[#FFD700] text-center mb-2">
                    {roundLabels(Number(roundNum), maxRound)}
                  </h3>
                  <div className="flex flex-col justify-around flex-1 gap-4">
                    {rounds[roundNum].map(match => {
                      const isMyMatch = (match.player1?.player_id === playerId || match.player2?.player_id === playerId);
                      const borderColor = match.status === 'active' ? '#FF9500' : match.status === 'completed' ? '#14F195' : '#333';
                      return (
                        <div key={match.match_id}
                          className={`w-56 rounded-lg border-2 overflow-hidden ${isMyMatch ? 'ring-2 ring-[#9945FF]' : ''}`}
                          style={{ borderColor }}
                          data-testid={`match-${match.match_id}`}>
                          {/* Player 1 */}
                          <div className={`flex items-center justify-between px-3 py-2 ${
                            match.winner === match.player1?.player_id ? 'bg-[#14F195]/10' : 'bg-[#1a1a1a]'
                          }`}>
                            <div className="flex items-center gap-2 min-w-0">
                              {match.winner === match.player1?.player_id && <Trophy className="w-3 h-3 text-[#FFD700] flex-shrink-0" />}
                              <span className={`text-sm truncate ${
                                match.player1 ? (match.player1.eliminated ? 'text-[#666] line-through' : 'text-white') : 'text-[#333]'
                              }`}>
                                {match.player1?.name || 'TBD'}
                              </span>
                              {match.player1?.player_id === playerId && <span className="text-[#14F195] text-[10px]">you</span>}
                            </div>
                            {match.player1_score !== null && (
                              <span className="font-game text-xs text-[#FFD700] ml-2">{match.player1_score}</span>
                            )}
                          </div>
                          {/* Divider */}
                          <div className="h-px bg-[#333] flex items-center justify-center relative">
                            <span className="absolute bg-[#0a0a0a] px-1 text-[8px] text-[#666]">VS</span>
                          </div>
                          {/* Player 2 */}
                          <div className={`flex items-center justify-between px-3 py-2 ${
                            match.winner === match.player2?.player_id ? 'bg-[#14F195]/10' : 'bg-[#1a1a1a]'
                          }`}>
                            <div className="flex items-center gap-2 min-w-0">
                              {match.winner === match.player2?.player_id && <Trophy className="w-3 h-3 text-[#FFD700] flex-shrink-0" />}
                              <span className={`text-sm truncate ${
                                match.player2 ? (match.player2.eliminated ? 'text-[#666] line-through' : 'text-white') : 'text-[#333]'
                              }`}>
                                {match.player2?.name || 'TBD'}
                              </span>
                              {match.player2?.player_id === playerId && <span className="text-[#14F195] text-[10px]">you</span>}
                            </div>
                            {match.player2_score !== null && (
                              <span className="font-game text-xs text-[#FFD700] ml-2">{match.player2_score}</span>
                            )}
                          </div>
                          {/* Status bar */}
                          <div className="px-3 py-1 text-center flex items-center justify-between" style={{ backgroundColor: `${borderColor}15` }}>
                            <span className="text-[10px] font-game" style={{ color: borderColor }}>
                              {match.status === 'active' ? 'IN PROGRESS' : match.status === 'completed' ? 'COMPLETED' : 'PENDING'}
                            </span>
                            {match.status === 'active' && !isMyMatch && onSpectateMatch && (
                              <button onClick={() => onSpectateMatch(tournamentId, match.match_id)}
                                className="text-[8px] font-game text-[#FF9500] hover:text-white flex items-center gap-1"
                                data-testid={`watch-match-${match.match_id}`}>
                                <Eye className="w-3 h-3" /> WATCH
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentBracket;
